import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Animated, Easing, Text, TouchableOpacity, View } from "react-native";
import { observer } from "mobx-react";
import { AntDesign } from "@expo/vector-icons";
import Colors from "../../Resources/Color";
import { useTranslation } from "react-i18next";
import { useScreenDimensions, useDevice } from "../_layout";
import {
  openFloatingSearch,
  closeFloatingSearch,
} from "../Exercise/Search/GlobalExerciseSearchFloating";

/**
 * Helper function to determine suggestion context based on protocol slot
 * @param {string} protocolSlot - The protocol slot value (e.g., "primarySquat-string")
 * @returns {string|null} - The suggestion context ("squat-string", "benchPress-string", "deadlift-string", or null)
 */
const getSuggestionContext = (protocolSlot) => {
  if (!protocolSlot) return null;

  if (protocolSlot.toLowerCase().includes("squat")) {
    return "squat";
  } else if (protocolSlot.toLowerCase().includes("benchpress")) {
    return "benchPress";
  } else if (protocolSlot.toLowerCase().includes("deadlift")) {
    return "deadlift";
  }

  return null;
};

/**
 * SlotTextNew
 *
 * Slot component with a clean '+' button that opens ExerciseSearchFloating
 * for exercise/modifier search.
 *
 * Props:
 * - slotExercise: the slot exercise object (can be null)
 * - selectSlot: function to handle slot selection
 * - useCTAColors: boolean for CTA color styling
 * - calendar: boolean indicating if in calendar view
 * - block: the block object
 * - protocolTitle: the protocol title string (e.g., "primarySquat-string")
 * - week: the week number for this slot
 */
const SlotTextNew = observer(
  ({
    slotExercise,
    selectSlot,
    useCTAColors,
    calendar,
    block,
    protocolTitle,
    week,
    onRegisterOpenSearch,
    onExerciseSetFinished,
  }) => {
    const { t, i18n } = useTranslation();
    const { screenSize } = useScreenDimensions();
    const { isMobile } = useDevice();

    const [isHovered, setIsHovered] = useState(false);
    const [isPlusHovered, setIsPlusHovered] = useState(false);

    const buttonRef = useRef(null);
    const secondAddButtonRef = useRef(null);

    // Stable per-component id for empty slots so multiple empty slots don't share the same instanceId
    const emptySlotLocalIdRef = useRef(
      `empty-${Math.random().toString(36).slice(2, 10)}`
    );

    // Generate a stable instance ID for this slot
    const instanceId = useMemo(() => {
      const blockId = block?.id || "unknown";
      const slotKey = slotExercise?.id || emptySlotLocalIdRef.current;
      return `slot-search-${blockId}-${slotKey}`;
    }, [block?.id, slotExercise?.id]);

    // Determine suggestion context based on protocol slot
    // Use protocolTitle (from parent ProtocolRow) instead of slotExercise.protocolSlot
    // because slotExercise is null when the slot is empty
    const suggestionContext = useMemo(() => {
      const context = getSuggestionContext(protocolTitle);
      return context;
    }, [protocolTitle]);

    // Get coach store
    const coach = block?.client?.coach ?? block?.coach;
    const isSearchOpen = coach?.activeFloatingSearchId === instanceId;
    const isSecondSearchOpen =
      coach?.activeFloatingSearchId === `${instanceId}-second`;

    // Refs for the exercise text buttons
    const primaryExerciseRef = useRef(null);
    const secondExerciseRef = useRef(null);

    const secondProtocolReferenceElement = slotExercise?.getSecondProtocol
      ? secondExerciseRef.current
      : secondAddButtonRef.current;

    const shouldShowSecondAddButton =
      ((isHovered || isSecondSearchOpen) && !isSearchOpen) || isMobile;

    // On mobile we always want the second-protocol '+' to be visible.
    // Initialize the animated value accordingly to avoid a hide->show flash
    // when components remount (common during slot selection updates).
    const secondAddButtonAnim = useRef(
      new Animated.Value(isMobile ? 1 : 0)
    ).current;

    useEffect(() => {
      // Keep the '+' always rendered/visible on mobile (no delayed animation).
      if (isMobile) {
        secondAddButtonAnim.stopAnimation();
        secondAddButtonAnim.setValue(1);
        return;
      }

      Animated.timing(secondAddButtonAnim, {
        toValue: shouldShowSecondAddButton ? 1 : 0,
        delay: 400,
        duration: 200,
        easing: Easing.out(Easing.cubic),
        useNativeDriver: false,
      }).start();
    }, [isMobile, secondAddButtonAnim, shouldShowSecondAddButton]);

    // Use refs to always have the latest callback versions (avoid stale closure issues)
    const handleSelectExerciseRef = useRef(null);

    const handleCloseSearch = useCallback(() => {
      setIsHovered(false);
      setIsPlusHovered(false);
      // Simply close the search - no need to check ownership since this is only
      // called from our own search context (via onClose or after selection)
      closeFloatingSearch(coach);
    }, [coach]);

    const handleSelectExercise = useCallback(
      (item, forSecondProtocol = false) => {
      if (!block || !item) {
        handleCloseSearch();
        return;
      }

        // Determine if we're working with a second protocol
        const isSecondProtocol = forSecondProtocol;

      if (slotExercise) {
        if (isSecondProtocol) {
          // Working with second protocol
          if (slotExercise.getSecondProtocol) {
            // Update existing second protocol
            block.updateSlotFromSearchResult(
              slotExercise.getSecondProtocol,
              item,
              t,
              true
            );
          } else {
            // Create new second protocol on the primary exercise
            block.addSlotFromSearchResult(
              item,
              t,
              protocolTitle,
              week,
              true,
              slotExercise
            );
          }
        } else {
          // Update existing primary slot exercise with new selection
          block.updateSlotFromSearchResult(slotExercise, item, t, false);
        }
      } else {
        // Create new primary slot with the selected exercise and modifiers
          block.addSlotFromSearchResult(item, t, protocolTitle, week, false, null);
      }

      handleCloseSearch();

      // Let parent know we've finished setting the exercise (so it can gate focus).
      try {
        onExerciseSetFinished?.({ isSecondProtocol: !!isSecondProtocol });
      } catch (e) {
        // no-op
      }
      },
      [block, slotExercise, t, protocolTitle, week, handleCloseSearch, onExerciseSetFinished]
    );

    // Keep ref updated with latest callback
    useEffect(() => {
      handleSelectExerciseRef.current = handleSelectExercise;
    }, [handleSelectExercise]);

    const handleOpenSearch = useCallback(
      (forSecondProtocol = false) => {
        if (!coach?.setProperty) return;

        setIsHovered(false);
        setIsPlusHovered(false);

        const searchInstanceId = forSecondProtocol
          ? `${instanceId}-second`
          : instanceId;

        // Determine the initial text
        let initialText = "";
        if (slotExercise) {
          initialText = forSecondProtocol
            ? slotExercise.getSecondProtocol?.getName(t, i18n.language) || ""
            : slotExercise.getName(t, i18n.language);
        }

        // Determine the reference element
        let referenceElement;
        if (forSecondProtocol) {
          referenceElement = slotExercise?.getSecondProtocol
            ? secondExerciseRef.current
            : secondAddButtonRef.current;
        } else {
          referenceElement = slotExercise
            ? primaryExerciseRef.current
            : buttonRef.current;
        }

        // Determine the slot exercise for this search
        const searchSlotExercise = forSecondProtocol
          ? slotExercise?.getSecondProtocol || slotExercise
          : slotExercise;

        // Use ref to always call the latest handleSelectExercise
        const onSelectExerciseCallback = (item) => {
          if (handleSelectExerciseRef.current) {
            handleSelectExerciseRef.current(item, forSecondProtocol);
          }
        };

        openFloatingSearch(coach, {
          instanceId: searchInstanceId,
          initialSearchText: initialText,
          referenceElement,
          onSelectExercise: onSelectExerciseCallback,
          onClose: handleCloseSearch,
          suggestionContext,
          crossAxisOffset: 0,
          slotExercise: searchSlotExercise,
          primarySlotExercise: slotExercise,
          block,
          protocolSlot: protocolTitle,
          currentWeek: week,
          suggestionsMode: "protocol",
          currentWorkout: null,
          exerciseIndex: 0,
        });
      },
      [
        coach,
        instanceId,
        slotExercise,
        t,
        i18n.language,
        suggestionContext,
        block,
        protocolTitle,
        week,
        handleCloseSearch,
      ]
    );

    useEffect(() => {
      if (onRegisterOpenSearch) {
        onRegisterOpenSearch(handleOpenSearch);
        return () => onRegisterOpenSearch(null);
      }
    }, [handleOpenSearch, onRegisterOpenSearch]);

    // If this view unmounts (e.g., user navigates away), ensure any floating
    // search owned by this slot is closed. This prevents FloatingUI from
    // rendering at (0,0) with a stale/missing reference element.
    // We store the values in refs so the cleanup has access to current values.
    const instanceIdRef = useRef(instanceId);
    const coachRef = useRef(coach);
    useEffect(() => {
      instanceIdRef.current = instanceId;
      coachRef.current = coach;
    }, [instanceId, coach]);

    useEffect(() => {
      return () => {
        const currentCoach = coachRef.current;
        const currentInstanceId = instanceIdRef.current;
        if (!currentCoach) return;

        const activeId = currentCoach.activeFloatingSearchId;
        const ownsActiveId =
          activeId === currentInstanceId ||
          activeId === `${currentInstanceId}-second`;

        if (ownsActiveId) {
          closeFloatingSearch(currentCoach);
        }
      };
    }, []);

    return (
      <View
        style={{
          alignItems: "center",
          flex: 1,
        }}
        onMouseEnter={() => {
          if (!isMobile) {
            setIsHovered(true);
          }
        }}
        onMouseLeave={() => setIsHovered(false)}
      >
        {slotExercise ? (
          <>
            <TouchableOpacity
              ref={primaryExerciseRef}
              onPress={() => handleOpenSearch(false)}
              style={{
                width: "100%",
                outlineStyle: "none",
              }}
            >
              <Text
                numberOfLines={2}
                style={{
                  width: "100%",
                  color: isSearchOpen ? Colors.bubbleColor : "black",
                  textAlign: "center",
                  fontSize: screenSize === "small" ? 11 : 11,
                  padding: 2,
                }}
              >
                {slotExercise.getName(t, i18n.language)}
              </Text>
            </TouchableOpacity>

            {slotExercise.getSecondProtocol && (
              <>
                <Text
                  style={{
                    width: "100%",
                    color: "black",
                    textAlign: "center",
                    fontSize: screenSize === "small" ? 11 : 11,
                    padding: 2,
                  }}
                >
                  +
                </Text>
                <TouchableOpacity
                  ref={secondExerciseRef}
                  onPress={() => handleOpenSearch(true)}
                  style={{
                    width: "100%",
                    outlineStyle: "none",
                  }}
                >
                  <Text
                    numberOfLines={2}
                    style={{
                      width: "100%",
                      color: isSecondSearchOpen ? Colors.bubbleColor : "black",
                      textAlign: "center",
                      fontSize: screenSize === "small" ? 11 : 11,
                      padding: 2,
                    }}
                  >
                    {slotExercise.getSecondProtocol.getName(t, i18n.language)}
                  </Text>
                </TouchableOpacity>
              </>
            )}

            {!slotExercise.getSecondProtocol && (
              <Animated.View
                pointerEvents={shouldShowSecondAddButton ? "auto" : "none"}
                style={{
                  width: "50%",
                  overflow: "hidden",
                  height: secondAddButtonAnim.interpolate({
                    inputRange: [0, 1],
                    outputRange: [0, 16],
                  }),
                  marginTop: secondAddButtonAnim.interpolate({
                    inputRange: [0, 1],
                    outputRange: [0, 5],
                  }),
                  opacity: secondAddButtonAnim,
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <TouchableOpacity
                  ref={secondAddButtonRef}
                  onPress={() => {
                    handleOpenSearch(true);
                    setIsHovered(false);
                  }}
                  style={{
                    width: "100%",
                    alignItems: "center",
                    backgroundColor: isSecondSearchOpen
                      ? Colors.bubbleColor
                      : Colors.lightGrayColor,
                    borderRadius: 10,
                    outlineStyle: "none",
                  }}
                >
                  <AntDesign
                    name="plus"
                    size={8}
                    color={
                      isSecondSearchOpen ? Colors.whiteColor : Colors.blackColor
                    }
                  />
                </TouchableOpacity>
              </Animated.View>
            )}
          </>
        ) : (
          <View
            style={{
              width: "100%",
              maxWidth: 128,
            }}
          >
            <TouchableOpacity
              ref={buttonRef}
              onPress={() => handleOpenSearch(false)}
              style={{
                width: "100%",
                alignItems: "center",
                justifyContent: "center",
                backgroundColor:
                  isSearchOpen || useCTAColors
                    ? Colors.bubbleColor
                    : Colors.lightGrayColor,
                borderRadius: 10,
                // paddingVertical: 8,
                // paddingHorizontal: 8,
                outlineStyle: "none",
                opacity: isPlusHovered ? 0.8 : 1,
                // height: 8,
              }}
              onMouseEnter={() => !isMobile && setIsPlusHovered(true)}
              onMouseLeave={() => setIsPlusHovered(false)}
            >
              <AntDesign
                name="plus"
                size={10}
                color={
                  isSearchOpen || useCTAColors
                    ? Colors.whiteColor
                    : Colors.blackColor
                }
              />
            </TouchableOpacity>

          </View>
        )}
      </View>
    );
  }
);

export default SlotTextNew;
